<template>
  <div class="emat-tab-container">
    <div
      v-for="(tab, i) in tabs"
      :key="i"
      class="emat-tab"
      :class="{
          active: tabActive(tab, i),
        }"
      @click="changeValue(tab, i)"
    >
      <template v-if="typeof(tab) === 'object'">
        <div class="emat-text" v-text="tab.label"></div>
        <div class="emat-extra" v-if="tab.extra" v-text="tab.extra"></div>
      </template>
      <div v-else v-text="tab"></div>
    </div>
    <div class="emat-active-container">
      <div class="emat-active-tab" :style="activeTabStyling"></div>
    </div>
  </div>
</template>

<script>
/**
 * Maintain around 160px for each option.
 */
export default {
  name: 'Tabs',
  props: {
    /**
     * An array containing strings or objects with the properties label, extra
     */
    tabs: {
      type: Array,
      required: true,
    },
    /**
     * The current selected tab (must correspond with the key of one of the tabs)
     * Defaults to the first tab
     */
    value: {
      required: false,
      default: 0,
    },
  },
  computed: {
    activeTabStyling() {
      return {
        width: this.tabWidth + '%',
        left: this.tabIndex !== null ? (this.tabIndex * this.tabWidth) + '%' : '-' + this.tabWidth + '%',
        opacity: this.tabIndex !== null ? 1 : 0,
      };
    },
    tabWidth() {
      return (100 / this.tabs.length);
    },
    tabIndex() {
      for (let i = 0; i < this.tabs.length; i++) {
        if (this.tabActive(this.tabs[i], i)) {
          return i;
        }
      }

      return null;
    },
  },
  methods: {
    changeValue(tab, i) {
      let newValue = i;
      if (typeof tab === 'object') {
        if (tab.hasOwnProperty('value')) {
          newValue = tab.value;
        } else if (tab.hasOwnProperty('id')) {
          newValue = tab.id;
        }
      }

      let oldValue = this.value;
      /**
       * Output the input for v-model
       */
      this.$emit('input', newValue);

      /**
       * Output the change with the new and old values
       */
      this.$emit('change', newValue, oldValue);
    },

    /**
     * Tab is object with key: value or id
     * @param {*} tab
     *
     * @param {number} i
     */
    tabActive(tab, i) {
      let tabValue = i;
      if (typeof tab === 'object') {
        if (tab.hasOwnProperty('value')) {
          tabValue = tab.value;
        } else if (tab.hasOwnProperty('id')) {
          tabValue = tab.id;
        }
      }

      return this.value === tabValue;
    },
  },
  created() {
    if (this.value === null) {
      this.$emit('input', Object.keys(this.tabs)[0]);
    }
  },
};
</script>

<style lang="scss" scoped>
@import "../../../assets/sass/abstracts";
.emat-tab-container {
  position: relative;
  background-color: white;
  color: var(--c-primary);
  margin: 9.5px 0 0;
  border-radius: $border-radius;
  display: flex;
  align-content: stretch;
  border: 1px solid rgba(0, 0, 0, .2);
  font-family: var(--font-black);
  overflow: hidden;
  user-select: none;

  .emat-tab {
    flex: 1 1 0;
    border-radius: calc(#{$border-radius} - 1px);
    padding: 0;
    cursor: pointer;
    text-align: center;
    font-size: 11px;
    line-height: 9px;
    font-family: var(--font-black);
    position: relative;
    z-index: 1;
    transition: background-color .3s;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    min-height: 30px;

    .emat-extra {
      margin-top: 2px;
      color: var(--c-primary-lighter);
    }

    .emat-text {
      position: relative;
      top: 1px;
    }

    &.active {
      color: var(--c-tab-active);
      transition: all .4s ease .2s;
    }
  }

  .emat-active-container {
    position: absolute;
    top: 2px;
    right: 2px;
    bottom: 2px;
    left: 2px;

    .emat-active-tab {
      position: absolute;
      background: var(--c-primary);
      top: 0;
      bottom: 0;
      border-radius: calc(#{$border-radius} - 1px);
      transition: all .4s ease;
    }
  }
}
</style>
